import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wand2, Copy, CheckCircle2, ArrowRight, Shield, History, Sparkles, RefreshCw, ChevronRight, Clock } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useMessageRewriter, ToneType, RewriteResult } from '../hooks/useMessageRewriter';
import { UpgradeBottomSheet } from '../components/Tools/UpgradeBottomSheet';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { DailyRewriteReminder } from '../components/Tools/DailyRewriteReminder';
import { GoogleReviewPrompt } from '../components/Tools/GoogleReviewPrompt';
import { RewriteCompleteModal } from '../components/Tools/RewriteCompleteModal';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Link } from 'react-router-dom';
const TONES: {
  id: ToneType;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
  glow: string;
  accent: string;
}[] = [{
  id: 'Calm',
  label: 'Calm',
  description: 'De-escalate tension',
  icon: Sparkles,
  color: 'text-blue-400',
  bg: 'bg-blue-500/10',
  border: 'border-blue-500/30',
  glow: 'shadow-[0_0_15px_rgba(59,130,246,0.15)]',
  accent: 'bg-blue-500'
}, {
  id: 'Assertive',
  label: 'Assertive',
  description: 'Stand your ground',
  icon: ArrowRight,
  color: 'text-amber-400',
  bg: 'bg-amber-500/10',
  border: 'border-amber-500/30',
  glow: 'shadow-[0_0_15px_rgba(245,158,11,0.15)]',
  accent: 'bg-amber-500'
}, {
  id: 'Flirty',
  label: 'Flirty',
  description: 'Lighten the mood',
  icon: Sparkles,
  color: 'text-pink-400',
  bg: 'bg-pink-500/10',
  border: 'border-pink-500/30',
  glow: 'shadow-[0_0_15px_rgba(236,72,153,0.15)]',
  accent: 'bg-pink-500'
}, {
  id: 'Boundary',
  label: 'Boundary',
  description: 'Set clear limits',
  icon: Shield,
  color: 'text-[#FF0000]',
  bg: 'bg-[#FF0000]/10',
  border: 'border-[#FF0000]/40',
  glow: 'shadow-[0_0_15px_rgba(255,0,0,0.15)]',
  accent: 'bg-[#FF0000]'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 60,
      damping: 15
    }
  }
};
function AnimatedScore({
  score
}: {
  score: number;
}) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = score / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= score) {
        setDisplayScore(score);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [score]);
  return <>{displayScore}</>;
}
// ── Rewrite state machine ──────────────────────────────────────────
type RewritePhase = 'idle' | 'loading' | 'success' | 'error';
export function MessageRewriterPage() {
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    rewriteMessage,
    history,
    loading,
    error,
    clearError
  } = useMessageRewriter();
  // ── Core state ──
  const [inputText, setInputText] = useState('');
  const [selectedTone, setSelectedTone] = useState<ToneType>('Calm');
  const [result, setResult] = useState<{
    text: string;
    score?: number;
  } | null>(null);
  // ── UI state ──
  const [copiedId, setCopiedId] = useState<string | null>(null); // tracks which item was copied
  const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [isPro, setIsPro] = useState(true);
  const [sessionRewriteCount, setSessionRewriteCount] = useState(0);
  const [loadingPhase, setLoadingPhase] = useState(0);
  // ── Refs for debounce & tracking ──
  const lastRewrittenTextRef = useRef('');
  const lastRewrittenToneRef = useRef<ToneType | null>(null);
  const isRewritingRef = useRef(false); // debounce guard
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingPhases = ['Analyzing tone...', 'Detecting patterns...', 'Crafting response...'];
  // ── Derive phase from hook state ──
  const phase: RewritePhase = loading ? 'loading' : error ? 'error' : result ? 'success' : 'idle';
  // ── Check pro status ──
  useEffect(() => {
    async function checkProStatus() {
      if (!user?.id) return;
      try {
        const {
          data
        } = await supabase.from('profiles').select('is_pro').eq('id', user.id).single();
        if (data) setIsPro(data.is_pro);
      } catch (err) {
        console.error('Error checking pro status:', err);
      }
    }
    checkProStatus();
  }, [user?.id]);
  // ── Error handling ──
  useEffect(() => {
    if (error) {
      if (error.isLimitError) {
        setShowUpgradeSheet(true);
      } else {
        showToast(error.message || 'Something went wrong. Please try again.', 'error', 5000);
      }
      clearError();
    }
  }, [error, showToast, clearError]);
  // ── Auto-rewrite on tone change (debounced) ──
  useEffect(() => {
    if (result && inputText.length >= 5 && inputText === lastRewrittenTextRef.current && selectedTone !== lastRewrittenToneRef.current && !loading && !isRewritingRef.current) {
      handleRewrite();
    }
  }, [selectedTone]);
  // ── Loading phase cycling ──
  useEffect(() => {
    if (!loading) {
      setLoadingPhase(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingPhase(p => (p + 1) % loadingPhases.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [loading]);
  // ── Cleanup copied timer on unmount ──
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);
  // ── Core rewrite handler with debounce guard ──
  const handleRewrite = useCallback(async () => {
    if (inputText.length < 5 || loading || isRewritingRef.current) return;
    isRewritingRef.current = true;
    try {
      const rewriteResult = await rewriteMessage(inputText, selectedTone);
      if (rewriteResult) {
        setResult({
          text: rewriteResult.rewritten_text,
          score: rewriteResult.toxicity_score
        });
        lastRewrittenTextRef.current = inputText;
        lastRewrittenToneRef.current = selectedTone;
        setCopiedId(null);
        setSessionRewriteCount(prev => prev + 1);
        // Show the modal — NO toast here to avoid z-index collision
        setShowRewriteModal(true);
      }
    } catch (err) {
      // Error is handled by the hook's error state + our useEffect above
      console.error('Rewrite failed:', err);
    } finally {
      isRewritingRef.current = false;
    }
  }, [inputText, selectedTone, loading, rewriteMessage]);
  // ── Copy handler with per-item tracking ──
  const handleCopy = useCallback(async (textToCopy: string, itemId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(itemId);
      showToast('Copied to clipboard', 'success', 2000);
      // Clear after 2s
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      showToast('Failed to copy — try again', 'error', 3000);
    }
  }, [showToast]);
  const handleHistoryClick = useCallback((item: RewriteResult) => {
    setInputText(item.original_text);
    setSelectedTone(item.tone);
    setResult({
      text: item.rewritten_text,
      score: item.toxicity_score
    });
    lastRewrittenTextRef.current = item.original_text;
    lastRewrittenToneRef.current = item.tone;
    document.getElementById('rewriter-input')?.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  function getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins || 1} MIN AGO`;
    if (diffHours < 24) return `${diffHours} HR AGO`;
    if (diffDays < 7) return `${diffDays} DAY${diffDays !== 1 ? 'S' : ''} AGO`;
    return `${Math.floor(diffDays / 7)} WK AGO`;
  }
  const canRewrite = inputText.length >= 5 && !loading && !isRewritingRef.current;
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative pb-24" data-id="element-9270">
      <style data-id="element-9271">{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes border-pulse {
          0%, 100% { border-color: rgba(255, 0, 0, 0.2); }
          50% { border-color: rgba(255, 0, 0, 0.6); }
        }
        .animate-border-pulse {
          animation: border-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>

      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9272" />

      <WebsiteHeader data-id="element-9273" />

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8 relative z-10" data-id="element-9274">
        {!isPro && <DailyRewriteReminder data-id="element-9275" />}

        <motion.div variants={containerVariants} initial="hidden" animate="visible" data-id="element-9276">
          {/* Hero Section */}
          <motion.div variants={itemVariants} className="mb-10 text-center sm:text-left" data-id="element-9277">
            <h1 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight uppercase" data-id="element-9278">
              Message <span className="text-[#FF0000]" data-id="element-9279">Rewriter</span>
            </h1>
            <p className="text-gray-400 font-mono text-sm leading-relaxed max-w-2xl" data-id="element-9280">
              Instantly transform emotionally charged messages into clear,
              effective communication.
            </p>

            {!isPro && <div className="mt-5 inline-flex items-center bg-[#0a0a0a] border border-gray-800 px-4 py-2 rounded-lg" data-id="element-9281">
                <span className="text-sm mr-3" data-id="element-9282">🎯</span>
                <span className="text-xs font-mono text-gray-300 uppercase tracking-wider" data-id="element-9283">
                  You have 3 free rewrites today
                </span>
              </div>}
          </motion.div>

          {/* Main Tool Area */}
          <div className="space-y-6" id="rewriter-input" data-id="element-9284">
            {/* Input Area */}
            <motion.div variants={itemVariants} data-id="element-9285">
              <div className="flex items-center mb-3" data-id="element-9286">
                <div className="w-5 h-5 flex-shrink-0 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/30 flex items-center justify-center mr-3" data-id="element-9287">
                  <span className="text-[#FF0000] font-bold text-[10px] font-mono" data-id="element-9288">
                    1
                  </span>
                </div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono" data-id="element-9289">
                  Draft Your Message
                </span>
              </div>

              <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden transition-colors relative group" data-id="element-9290">
                {/* Focus Pulse Border */}
                <div className="absolute inset-0 border border-transparent group-focus-within:animate-border-pulse rounded-xl pointer-events-none z-10" data-id="element-9291" />

                {/* Corner Brackets */}
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-focus-within:border-[#FF0000] transition-colors z-20" data-id="element-9292" />
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-focus-within:border-[#FF0000] transition-colors z-20" data-id="element-9293" />
                <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-focus-within:border-[#FF0000] transition-colors z-20" data-id="element-9294" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-focus-within:border-[#FF0000] transition-colors z-20" data-id="element-9295" />

                <textarea id="message-input" value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Paste your raw or emotional message here..." aria-label="Message to rewrite" className="w-full h-40 bg-transparent text-white p-6 resize-none outline-none font-mono text-sm leading-relaxed placeholder:text-gray-600 relative z-20" data-id="element-9296" />
                <div className="px-6 py-3 border-t border-gray-800 bg-black/40 flex justify-between items-center relative z-20" data-id="element-9297">
                  <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-9298">
                    {inputText.length > 0 && inputText.length < 5 ? 'Minimum 5 characters' : ''}
                  </span>
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${inputText.length < 5 ? 'text-red-500' : 'text-gray-500'}`} data-id="element-9299">
                    {inputText.length < 5 ? `${inputText.length} / 5 min` : `${inputText.length} chars`}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Tone Selector */}
            <motion.div variants={itemVariants} data-id="element-9300">
              <div className="flex items-center mb-3" data-id="element-9301">
                <div className="w-5 h-5 flex-shrink-0 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/30 flex items-center justify-center mr-3" data-id="element-9302">
                  <span className="text-[#FF0000] font-bold text-[10px] font-mono" data-id="element-9303">
                    2
                  </span>
                </div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono" data-id="element-9304">
                  Select Tone
                </span>
              </div>

              <div className="flex overflow-x-auto md:grid md:grid-cols-4 gap-3 pb-4 md:pb-0 hide-scrollbar" role="group" aria-label="Tone selection" data-id="element-9305">
                {TONES.map(tone => {
                const isSelected = selectedTone === tone.id;
                const Icon = tone.icon;
                return <button key={tone.id} onClick={() => setSelectedTone(tone.id)} aria-pressed={isSelected} disabled={loading} className={`
                        flex flex-col flex-shrink-0 w-40 md:w-auto min-h-[110px] p-4 rounded-xl border transition-all duration-200 text-left relative overflow-hidden
                        ${loading ? 'opacity-60 cursor-not-allowed' : ''}
                        ${isSelected ? `${tone.bg} ${tone.border} ${tone.glow} scale-[1.02]` : 'bg-[#0a0a0a] border-gray-800 hover:border-gray-600 hover:bg-gray-900'}
                      `} data-id="element-9306">
                      <div className="flex justify-between items-start w-full mb-3" data-id="element-9307">
                        <div className={`p-1.5 rounded-md ${isSelected ? tone.bg : 'bg-gray-800'}`} data-id="element-9308">
                          <Icon size={14} className={isSelected ? tone.color : 'text-gray-400'} data-id="element-9309" />
                        </div>
                        {isSelected && <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20
                    }} data-id="element-9310">
                            <CheckCircle2 className={`w-4 h-4 ${tone.color}`} data-id="element-9311" />
                          </motion.div>}
                      </div>
                      <div data-id="element-9312">
                        <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${isSelected ? tone.color : 'text-gray-300'}`} data-id="element-9313">
                          {tone.label}
                        </div>
                        <div className="text-[10px] font-mono text-gray-500 leading-tight" data-id="element-9314">
                          {tone.description}
                        </div>
                      </div>
                    </button>;
              })}
              </div>
            </motion.div>

            {/* Action Button */}
            <motion.div variants={itemVariants} data-id="element-9315">
              <motion.button whileHover={canRewrite ? {
              scale: 1.01
            } : {}} whileTap={canRewrite ? {
              scale: 0.99
            } : {}} onClick={handleRewrite} disabled={!canRewrite} aria-busy={loading} className={`
                  w-full py-4 rounded-xl font-bold uppercase tracking-wider transition-all flex items-center justify-center
                  ${inputText.length < 5 ? 'bg-gray-900 text-gray-600 cursor-not-allowed border border-gray-800' : loading ? 'bg-[#FF0000]/50 text-white cursor-wait' : 'bg-[#FF0000] hover:bg-gradient-to-r hover:from-[#FF0000] hover:to-red-600 text-white shadow-[0_0_20px_rgba(255,0,0,0.3)]'}
                `} data-id="element-9316">
                {loading ? <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" data-id="element-9317" />
                    Analyzing & rewriting...
                  </> : <>
                    <Wand2 className="w-5 h-5 mr-2" data-id="element-9318" />
                    Rewrite Message
                  </>}
              </motion.button>
            </motion.div>

            {/* Output Area — consistent min-height prevents layout jump */}
            <div className="min-h-[220px]" data-id="element-9319">
              <AnimatePresence mode="wait" data-id="element-9320">
                {phase === 'idle' && <motion.div key="empty" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="border border-gray-800 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center bg-[#0a0a0a]/30 min-h-[200px]" data-id="element-9321">
                    <Wand2 className="w-6 h-6 text-gray-700 mb-3" data-id="element-9322" />
                    <p className="text-xs text-gray-500 font-mono uppercase tracking-widest" data-id="element-9323">
                      Your rewritten message will appear here
                    </p>
                  </motion.div>}

                {phase === 'loading' && <motion.div key="loading" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-8 relative overflow-hidden min-h-[200px] flex flex-col justify-center" data-id="element-9324">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" data-id="element-9325" />
                    <div className="flex flex-col items-center justify-center mb-8" data-id="element-9326">
                      <div className="flex items-center gap-1.5 mb-4" data-id="element-9327">
                        {[0, 150, 300].map((delay, i) => <span key={i} className="w-2 h-2 bg-[#FF0000] rounded-full animate-bounce" style={{
                      animationDelay: `${delay}ms`
                    }} data-id="element-9328" />)}
                      </div>
                      <AnimatePresence mode="wait" data-id="element-9329">
                        <motion.p key={loadingPhase} initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -5
                    }} className="text-gray-400 font-mono text-xs uppercase tracking-widest h-4" data-id="element-9330">
                          {loadingPhases[loadingPhase]}
                        </motion.p>
                      </AnimatePresence>
                    </div>
                    <div className="space-y-3 max-w-lg mx-auto w-full px-4" data-id="element-9331">
                      <div className="h-2.5 bg-gray-800/80 rounded-full w-full animate-pulse" style={{
                    animationDelay: '0ms'
                  }} data-id="element-9332" />
                      <div className="h-2.5 bg-gray-800/60 rounded-full w-[90%] animate-pulse" style={{
                    animationDelay: '150ms'
                  }} data-id="element-9333" />
                      <div className="h-2.5 bg-gray-800/40 rounded-full w-[75%] animate-pulse" style={{
                    animationDelay: '300ms'
                  }} data-id="element-9334" />
                    </div>
                  </motion.div>}

                {phase === 'success' && result && <motion.div key="result" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="space-y-6" data-id="element-9335">
                    <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl overflow-hidden shadow-xl relative group" data-id="element-9336">
                      {/* Corner Brackets */}
                      <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 z-10" data-id="element-9337" />
                      <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 z-10" data-id="element-9338" />
                      <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 z-10" data-id="element-9339" />
                      <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 z-10" data-id="element-9340" />

                      <div className="p-6 space-y-6" data-id="element-9341">
                        {/* Header & Score */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b border-gray-800/50 pb-6" data-id="element-9342">
                          <div className="flex-1" data-id="element-9343">
                            <div className="flex items-center mb-2" data-id="element-9344">
                              <motion.div initial={{
                            scale: 0,
                            rotate: -45
                          }} animate={{
                            scale: 1,
                            rotate: 0
                          }} transition={{
                            type: 'spring',
                            delay: 0.1
                          }} data-id="element-9345">
                                <Sparkles className="w-4 h-4 text-amber-400 mr-2" data-id="element-9346" />
                              </motion.div>
                              <span className="text-xs font-bold text-white uppercase tracking-widest font-mono mr-3" data-id="element-9347">
                                Analysis Complete
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${TONES.find(t => t.id === selectedTone)?.bg} ${TONES.find(t => t.id === selectedTone)?.color}`} data-id="element-9348">
                                {selectedTone}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 font-mono" data-id="element-9349">
                              Message successfully reframed.
                            </p>
                          </div>

                          {result.score !== undefined && <motion.div initial={{
                        scale: 0.9,
                        opacity: 0
                      }} animate={{
                        scale: 1,
                        opacity: 1
                      }} transition={{
                        delay: 0.2,
                        type: 'spring'
                      }} className="flex flex-col gap-2 w-full sm:w-48" data-id="element-9350">
                              <div className="flex items-end justify-between" data-id="element-9351">
                                <span className="text-xs font-mono text-gray-400 uppercase tracking-widest mb-1" data-id="element-9352">
                                  Health Score
                                </span>
                                <div className="flex items-baseline gap-1" data-id="element-9353">
                                  <span className="text-2xl font-black text-emerald-400 leading-none" data-id="element-9354">
                                    +<AnimatedScore score={result.score} data-id="element-9355" />%
                                  </span>
                                </div>
                              </div>
                              <div className="h-2 w-full bg-gray-900 rounded-full overflow-hidden" data-id="element-9356">
                                <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${Math.min(100, result.score)}%`
                          }} transition={{
                            duration: 1,
                            delay: 0.3,
                            ease: 'easeOut'
                          }} className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 rounded-full" data-id="element-9357" />
                              </div>
                            </motion.div>}
                        </div>

                        {/* Before / After Layout */}
                        <div className="space-y-4" data-id="element-9358">
                          {/* Original */}
                          <motion.div initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: 0.3
                      }} className="rounded-lg overflow-hidden border border-gray-800 bg-black/40" data-id="element-9359">
                            <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20" data-id="element-9360">
                              <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest font-mono" data-id="element-9361">
                                Original
                              </span>
                            </div>
                            <div className="p-4" data-id="element-9362">
                              <p className="text-sm text-gray-400 font-mono italic" data-id="element-9363">
                                "{lastRewrittenTextRef.current}"
                              </p>
                            </div>
                          </motion.div>

                          {/* Rewritten */}
                          <motion.div initial={{
                        opacity: 0,
                        x: 10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: 0.4
                      }} className="rounded-lg overflow-hidden border border-emerald-500/30 bg-black/60 shadow-[0_0_15px_rgba(16,185,129,0.05)]" data-id="element-9364">
                            <div className="px-4 py-2 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center justify-between" data-id="element-9365">
                              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest font-mono" data-id="element-9366">
                                Rewritten
                              </span>
                              {/* Inline copy icon for rewritten text */}
                              <button onClick={e => handleCopy(result.text, 'result-inline', e)} className="p-1 rounded-md text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all" title="Copy rewritten text" aria-label="Copy rewritten text" data-id="element-9367">
                                {copiedId === 'result-inline' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" data-id="element-9368" /> : <Copy className="w-3.5 h-3.5" data-id="element-9369" />}
                              </button>
                            </div>
                            <div className="p-5 min-h-[100px]" data-id="element-9370">
                              <p className="text-base text-white font-mono whitespace-pre-wrap leading-relaxed" data-id="element-9371">
                                {result.text}
                              </p>
                            </div>
                          </motion.div>
                        </div>
                      </div>

                      {/* Result Actions */}
                      <div className="p-4 border-t border-gray-800 bg-black/40 flex flex-col sm:flex-row justify-end gap-3" data-id="element-9372">
                        <button onClick={() => {
                      document.getElementById('rewriter-input')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                      setTimeout(() => document.getElementById('message-input')?.focus(), 500);
                    }} className="flex-1 sm:flex-none flex items-center justify-center px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all bg-transparent text-gray-400 hover:text-white border border-gray-800 hover:border-gray-600" data-id="element-9373">
                          <RefreshCw className="w-4 h-4 mr-2" data-id="element-9374" />
                          Rewrite Again
                        </button>
                        <button onClick={e => handleCopy(result.text, 'result-main', e)} className={`
                            flex-1 sm:flex-none flex items-center justify-center px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all
                            ${copiedId === 'result-main' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white text-black hover:bg-gray-200 shadow-[0_0_15px_rgba(255,255,255,0.1)]'}
                          `} data-id="element-9375">
                          {copiedId === 'result-main' ? <>
                              <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-9376" />
                              Copied
                            </> : <>
                              <Copy className="w-4 h-4 mr-2" data-id="element-9377" />
                              Copy to Clipboard
                            </>}
                        </button>
                      </div>
                    </div>

                    <GoogleReviewPrompt isVisible={sessionRewriteCount >= 2} data-id="element-9378" />
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* History Section */}
          <motion.div variants={itemVariants} className="mt-12" data-id="element-9379">
            <div className="flex items-center justify-between mb-6 border-b border-gray-800 pb-4" data-id="element-9380">
              <div className="flex items-center" data-id="element-9381">
                <History className="w-4 h-4 text-gray-500 mr-2" data-id="element-9382" />
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest font-mono" data-id="element-9383">
                  Recent Rewrites
                </h3>
              </div>
              {history.length > 0 && <Link to="/profile" className="text-[10px] font-bold text-gray-500 hover:text-white uppercase tracking-widest font-mono transition-colors" data-id="element-9384">
                  View All →
                </Link>}
            </div>

            {history.length === 0 ? <div className="text-center py-8 border border-gray-800 border-dashed rounded-xl bg-[#0a0a0a]/30" data-id="element-9385">
                <p className="text-xs text-gray-500 font-mono uppercase tracking-widest" data-id="element-9386">
                  No rewrites yet. Start by typing a message above.
                </p>
              </div> : <div className="space-y-4" data-id="element-9387">
                {history.slice(0, 5).map((item, idx) => {
              const historyItemId = item.id || `history-${idx}`;
              return <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.05
              }} key={historyItemId} onClick={() => handleHistoryClick(item)} role="button" tabIndex={0} onKeyDown={e => {
                if (e.key === 'Enter') handleHistoryClick(item);
              }} className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5 cursor-pointer hover:border-gray-600 hover:scale-[1.01] transition-all duration-200 group relative overflow-hidden flex items-center justify-between" data-id="element-9388">
                      {/* Tone Accent Line */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-200 group-hover:w-1.5 ${TONES.find(t => t.id === item.tone)?.accent || 'bg-gray-800'}`} data-id="element-9389" />

                      <div className="pl-2 flex-1 pr-4 min-w-0" data-id="element-9390">
                        <div className="flex justify-between items-center mb-3" data-id="element-9391">
                          <div className="flex items-center gap-3" data-id="element-9392">
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${TONES.find(t => t.id === item.tone)?.bg || 'bg-gray-800'} ${TONES.find(t => t.id === item.tone)?.color || 'text-gray-400'}`} data-id="element-9393">
                              {item.tone}
                            </span>
                            {item.toxicity_score !== undefined && <span className="text-[10px] text-emerald-500 font-mono font-bold" data-id="element-9394">
                                +{item.toxicity_score}%
                              </span>}
                          </div>
                          <div className="flex items-center text-gray-600" data-id="element-9395">
                            <Clock className="w-3 h-3 mr-1" data-id="element-9396" />
                            <span className="text-[10px] font-mono uppercase tracking-widest" data-id="element-9397">
                              {item.created_at ? getTimeAgo(item.created_at) : 'Just now'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3" data-id="element-9398">
                          <div className="flex items-start gap-3" data-id="element-9399">
                            <span className="text-[9px] font-bold text-red-500/70 uppercase tracking-widest font-mono mt-0.5 w-16 flex-shrink-0 text-right" data-id="element-9400">
                              Original
                            </span>
                            <p className="text-gray-500 font-mono text-xs line-clamp-1 italic flex-1" data-id="element-9401">
                              "{item.original_text}"
                            </p>
                          </div>
                          <div className="flex items-start gap-3 group/rewrite" data-id="element-9402">
                            <span className="text-[9px] font-bold text-emerald-500/70 uppercase tracking-widest font-mono mt-0.5 w-16 flex-shrink-0 text-right" data-id="element-9403">
                              Rewritten
                            </span>
                            <div className="flex-1 flex items-start justify-between gap-2" data-id="element-9404">
                              <p className="text-gray-300 font-mono text-sm line-clamp-2 group-hover:text-white transition-colors" data-id="element-9405">
                                {item.rewritten_text}
                              </p>
                              <button onClick={e => handleCopy(item.rewritten_text, historyItemId, e)} className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${copiedId === historyItemId ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700 opacity-0 group-hover/rewrite:opacity-100'}`} title="Copy rewritten text" aria-label={`Copy rewritten text for ${item.tone} rewrite`} data-id="element-9406">
                                {copiedId === historyItemId ? <CheckCircle2 className="w-3.5 h-3.5" data-id="element-9407" /> : <Copy className="w-3.5 h-3.5" data-id="element-9408" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex-shrink-0 text-gray-700 group-hover:text-white transition-colors" data-id="element-9409">
                        <ChevronRight className="w-5 h-5" data-id="element-9410" />
                      </div>
                    </motion.div>;
            })}
              </div>}
          </motion.div>
        </motion.div>
      </main>

      <UpgradeBottomSheet isOpen={showUpgradeSheet} onClose={() => setShowUpgradeSheet(false)} onUpgradeClick={() => setShowSubscriptionModal(true)} data-id="element-9411" />

      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={user?.user_metadata?.subscriptionPlan || 'free'} data-id="element-9412" />

      <RewriteCompleteModal isOpen={showRewriteModal} score={result?.score ?? 0} onViewResults={() => {
      setShowRewriteModal(false);
      setTimeout(() => {
        document.getElementById('rewriter-input')?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 150);
    }} onClose={() => setShowRewriteModal(false)} data-id="element-9413" />
    </div>;
}